import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Target, Eye } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import Seo from "@/components/seo/Seo";
import RichText from "@/components/RichText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Body strings authored via the RichTextEditor come in as HTML. Legacy team
// bios in the seed are plain strings — this helper picks the right renderer.
function isProbablyHtml(str) {
  return typeof str === "string" && /<\w+[\s>]/i.test(str);
}

// Hardcoded fallback used when the /api/team endpoint is unavailable or empty.
// The DB seed mirrors this list, so once the seed runs the DB is the source of
// truth and the client can edit team members via /admin/team.
const LEADERS_FALLBACK = [
  { name: "Rick Peters", title: "President & CEO", initials: "RP", bio: "" },
  { name: "Kert Hulett", title: "CTO", initials: "KH", bio: "" },
  { name: "Alfred Mondoux", title: "Chief Financial Officer", initials: "AM", bio: "" },
];

// Fallback content used when the /api/pages/built-in/why endpoint fails.
// Kept intentionally small — full defaults live on the backend seed.
const CONTENT_FALLBACK = {
  hero_title_prefix: "The Antera",
  hero_title_accent: "Story",
  hero_image: "/api/media/137f924c-d2c4-4e66-a6d7-d886eaa5a644",
  hero_image_alt: "Antera Software Headquarters in Plano, Texas",
  story_heading: "Innovation Rooted in Partnership",
  story_intro: "",
  story_pillars: [],
  mission_heading: "Our Mission",
  mission_body: "",
  vision_heading: "The Vision",
  vision_body: "",
  team_heading: "Our Leadership Team",
  cta_heading: "Ready to partner with Antera?",
  cta_body: "",
  cta_primary_label: "Request a Demo",
  cta_primary_href: "/contact",
  cta_secondary_label: "Contact Sales",
  cta_secondary_href: "/contact",
  seo_title: "Why Antera | 18 Years of Promo Industry Software Leadership",
  seo_description:
    "Why Antera Software is the leading promotional products software platform. 18+ years serving the promo and print industries with technology-driven ERP, CRM, and eCommerce.",
  seo_keywords:
    "leading promotional products software, technology driven promotional products software, best promotional product software, why antera, promotional products company software",
};

export default function WhyPage() {
  const [content, setContent] = useState(CONTENT_FALLBACK);
  const [teamMembers, setTeamMembers] = useState(null);

  useEffect(() => {
    // Fetch page content and team members in parallel. Both endpoints are
    // best-effort — the page falls back to hardcoded content on failure so it
    // is never blank.
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pages/built-in/why`);
        if (data && typeof data === "object") {
          setContent({ ...CONTENT_FALLBACK, ...data });
        }
      } catch { /* silent */ }
    })();
    (async () => {
      try {
        const { data } = await axios.get(`${API}/team`);
        if (Array.isArray(data) && data.length > 0) setTeamMembers(data);
      } catch { /* silent */ }
    })();
  }, []);

  const displayLeaders = teamMembers || LEADERS_FALLBACK;
  const pillars = Array.isArray(content.story_pillars) ? content.story_pillars : [];

  const heroImgSrc = content.hero_image?.startsWith("http")
    ? content.hero_image
    : content.hero_image
    ? content.hero_image
    : "";

  return (
    <div data-testid="why-page" className="page-transition">
      <Seo
        title={content.seo_title}
        description={content.seo_description}
        keywords={content.seo_keywords}
        path="/why"
      />

      {/* Hero */}
      <section className="pt-32 pb-14 lg:pt-40 lg:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1
              data-testid="why-hero-title"
              className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 text-center"
            >
              {content.hero_title_prefix}
              {content.hero_title_accent && (
                <>
                  {" "}
                  <span className="text-sky-600">{content.hero_title_accent}</span>
                </>
              )}
            </h1>
          </motion.div>

          {heroImgSrc && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mt-10 rounded-xl overflow-hidden shadow-lg"
            >
              <img
                src={heroImgSrc}
                alt={content.hero_image_alt || ""}
                className="w-full h-56 sm:h-72 lg:h-96 object-cover"
                data-testid="why-building-image"
              />
            </motion.div>
          )}
        </div>
      </section>

      {/* Innovation Rooted in Partnership */}
      <section className="py-14 lg:py-20 bg-slate-50/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2
              data-testid="why-story-heading"
              className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-6"
            >
              {content.story_heading}
            </h2>
            {content.story_intro && (
              <p className="text-base text-slate-600 leading-relaxed mb-6 whitespace-pre-line">
                {content.story_intro}
              </p>
            )}

            {pillars.length > 0 && (
              <div className="space-y-8 mt-10">
                {pillars.map((p, i) => (
                  <div
                    key={i}
                    data-testid={`why-pillar-${i}`}
                    className="border-l-[3px] border-sky-400 pl-6"
                  >
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{p.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{p.body}</p>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-slate-50 border border-slate-200 rounded-xl p-8 lg:p-10"
              data-testid="mission-card"
            >
              <div className="flex items-center gap-3 mb-4">
                <Target className="h-6 w-6 text-sky-600" />
                <h3 className="text-xl font-bold text-slate-900">{content.mission_heading}</h3>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                {content.mission_body}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-slate-900 rounded-xl p-8 lg:p-10"
              data-testid="vision-card"
            >
              <div className="flex items-center gap-3 mb-4">
                <Eye className="h-6 w-6 text-sky-400" />
                <h3 className="text-xl font-bold text-white">{content.vision_heading}</h3>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed whitespace-pre-line">
                {content.vision_body}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-14 lg:py-20 bg-slate-50/50" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              {content.team_heading}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayLeaders.map((leader, i) => (
              <motion.div
                key={leader.id || leader.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.4 }}
                data-testid={`leader-card-${i}`}
                className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-4">
                  {leader.photo_url ? (
                    <img
                      src={leader.photo_url}
                      alt={leader.name}
                      className="w-14 h-14 rounded-full object-cover border border-slate-200 shrink-0"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-sky-100 flex items-center justify-center text-sky-600 text-lg font-bold shrink-0">
                      {leader.initials || leader.name.slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h3 className="text-base font-bold text-slate-900">{leader.name}</h3>
                    <p className="text-sm text-sky-600 font-medium">{leader.title}</p>
                  </div>
                </div>
                {isProbablyHtml(leader.bio) ? (
                  <RichText
                    html={leader.bio}
                    className="prose prose-sm prose-slate max-w-none prose-p:text-slate-500 prose-a:text-sky-600 prose-a:no-underline hover:prose-a:underline text-sm"
                  />
                ) : (
                  <p className="text-sm text-slate-500 leading-relaxed">{leader.bio}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-sky-600 py-14 lg:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 data-testid="why-cta-heading" className="text-2xl sm:text-3xl font-bold text-white">
            {content.cta_heading}
          </h2>
          <p className="mt-3 text-sm text-sky-100 max-w-lg mx-auto">{content.cta_body}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            {content.cta_primary_label && (
              <Link to={content.cta_primary_href || "/contact"}>
                <Button
                  data-testid="why-bottom-demo-btn"
                  className="bg-white text-sky-700 hover:bg-sky-50 rounded-full px-8 h-12 text-sm font-semibold"
                >
                  {content.cta_primary_label} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            )}
            {content.cta_secondary_label && (
              <Link to={content.cta_secondary_href || "/contact"}>
                <Button
                  variant="outline"
                  data-testid="why-bottom-contact-btn"
                  className="border-white/40 text-white hover:bg-sky-700 rounded-full px-8 h-12 text-sm font-semibold"
                >
                  {content.cta_secondary_label}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
